// aiRuntime - 后台（Element Plus + axios）的 AI 运行时适配器注册
//
// 作用：把后台的存储 / 提示 / 接口 / Markdown 渲染 / TTS 实现一次性注册进
//       @huyu/core 的运行时上下文，此后 useChatAgent / useChatHistory / useChatFaq /
//       useVoiceChat / useChatRender 全部零参数可用（与 AI 前台共用同一份内核源码）。
//
// 约定：本文件是后台唯一「内核 → 宿主」耦合点；新增后台能力只需在此补一项。
import { markRaw } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Setting, InfoFilled, ChatDotRound, Promotion, Connection } from '@element-plus/icons-vue'
import { configureAiRuntime } from '@huyu/core/vue'

import { renderMarkdown } from '@/modules/huyu-framework/backend/utils/markdown'
import useTtsStore from '@/store/modules/tts'

import { listAgent, listRagStrategy, getAgent } from '@/modules/huyu-framework/backend/api/huyuAI/aiAgent'
import { listKnowledgeNoPage } from '@/modules/huyu-framework/backend/api/huyuAI/knowledge'
import {
  getListByUserApi,
  updateChat,
  getHistoryChatApi,
  deleteChatApi
} from '@/modules/huyu-framework/backend/api/huyuAI/chat'
import { flushChatMemory } from '@/modules/huyu-framework/backend/api/huyuAI/memory'
import { treeListFaq } from '@/modules/huyu-framework/backend/api/huyuAI/faq'
import {
  getSpeechConfig,
  speechKwsValidate,
  startKwsListening,
  stopKwsListening,
  createVadRecorder
} from '@/modules/huyu-framework/backend/api/huyuAI/speech'

/** 浏览器 localStorage 适配（隐私模式下读写会抛错，这里静默兜底） */
const browserStorage = {
  get(key) {
    try {
      return localStorage.getItem(key)
    } catch (e) {
      return null
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value)
    } catch (e) {
      /* 配额满：静默 */
    }
  },
  remove(key) {
    try {
      localStorage.removeItem(key)
    } catch (e) {
      /* ignore */
    }
  }
}

// TTS store 是懒读的：#app 挂载前 pinia 尚未激活，不能在此提前 useTtsStore()。
// 用 getter 代理一份，composable 里读 tts 字段时再取真实 store（此时必有活动 pinia）。
const ttsProxy = {
  get messageId() {
    return useTtsStore().messageId
  },
  get active() {
    return useTtsStore().active
  },
  get segText() {
    return useTtsStore().segText
  },
  get phase() {
    return useTtsStore().phase
  },
  toggle(messageId, text, options) {
    return useTtsStore().toggle(messageId, text, options)
  }
}

// 快捷问题卡片图标/配色轮换池（后台 Element Plus 图标）
const quickCardStyle = [
  { icon: markRaw(Setting), color: '#409eff' },
  { icon: markRaw(InfoFilled), color: '#67c23a' },
  { icon: markRaw(ChatDotRound), color: '#909399' },
  { icon: markRaw(Promotion), color: '#e6a23c' },
  { icon: markRaw(Connection), color: '#409eff' }
]

let configured = false

/** 幂等注册（每个 composable 入口各调一次，实际只生效一次） */
export function ensureAiRuntime() {
  if (configured) return
  configured = true
  configureAiRuntime({
    storage: browserStorage,
    notify: {
      success: (m) => ElMessage.success(m),
      error: (m) => ElMessage.error(m),
      warning: (m) => ElMessage.warning(m),
      info: (m) => ElMessage.info(m),
      confirm: (message, title, options) =>
        ElMessageBox.confirm(message, title || '提示', options || { type: 'warning' })
    },
    api: {
      listAgent,
      listRagStrategy,
      getAgent,
      listKnowledgeNoPage,
      getListByUserApi,
      updateChat,
      getHistoryChatApi,
      deleteChatApi,
      flushChatMemory,
      treeListFaq,
      getSpeechConfig,
      speechKwsValidate,
      startKwsListening,
      stopKwsListening,
      createVadRecorder
    },
    renderMarkdown,
    baseApi: import.meta.env.VITE_APP_BASE_API,
    tts: ttsProxy,
    quickCardStyle
  })
}

export default ensureAiRuntime
